import React from 'react';
import { CalendarIcon, MapPinIcon } from 'lucide-react';
import { ActionLink } from '../ui/ActionLink';
import type { Destination, LinkItem, Season } from '../../types/content';
interface DestinationHeroProps {
  destination: Destination;
  season: Season;
  /** The page's SEO heading, shown as the subtitle under the city name. */
  headline: string;
  lead: string;
  primary: LinkItem;
  secondary: LinkItem;
  stats: {
    label: string;
    value: string;
  }[];
}

// Location header: the city name leads, on an equal split with the photo, so every destination reads the same way.
export function DestinationHero({
  destination,
  season,
  headline,
  lead,
  primary,
  secondary,
  stats
}: DestinationHeroProps) {
  const seasonLabel = season === 'year-round' ? 'Year-round campus' : 'Seasonal programmes';
  return <section className="w-full bg-white p-2 md:p-4" aria-labelledby="destination-title" data-id="element-311" data-mp="mxuw1c4c4zxx">
      <div className="grid overflow-hidden rounded-[28px] bg-cream md:rounded-[40px] lg:min-h-[600px] lg:grid-cols-2" data-id="element-312" data-mp="r07udfgr5vko">
        <div className="order-2 flex flex-col justify-between gap-10 px-6 py-10 md:px-12 md:py-14 lg:order-1" data-id="element-313" data-mp="jjx5lx6qv91i">
          <div data-id="element-314" data-mp="ellvdlcjewir">
            <div className="flex flex-wrap items-center gap-2" data-id="element-315" data-mp="em5f97lqzuzf">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 type-small font-bold text-ink" data-id="element-316" data-mp="skv98ed9tejk">
                <MapPinIcon className="h-3.5 w-3.5 text-brand" aria-hidden="true" data-id="element-317" data-mp="ogxesgyw4rwu" />
                {destination.country}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 type-small font-bold text-white" data-id="element-318" data-mp="xjeyoor2p8go">
                <CalendarIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-319" data-mp="1c6hyj22vbt76" />
                {seasonLabel}
              </span>
            </div>

            <h1 id="destination-title" className="mt-8" data-id="element-320" data-mp="1l8hedr25cr8j">
              <span className="block type-hero text-ink" data-id="element-321" data-mp="1l998exg3igwp">{destination.name}</span>
              <span className="mt-3 block type-h4 font-normal normal-case text-brand-press" data-id="element-322" data-mp="1gafwvakdczz6">{headline}</span>
            </h1>
            <p className="mt-6 max-w-xl type-body text-body" data-id="element-323" data-mp="dxjljydxty1i">{lead}</p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-324" data-mp="iw4po8wb91v8">
              <ActionLink to={primary.to} data-id="element-325" data-mp="mw54hunbnf2t">{primary.label}</ActionLink>
              <ActionLink to={secondary.to} variant="secondary" data-id="element-326" data-mp="mutydo6elv8j">
                {secondary.label}
              </ActionLink>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-5 border-t border-line pt-6 sm:grid-cols-4" data-id="element-327" data-mp="wlooj3mrtez9">
            {stats.map(stat => <div key={stat.label} data-id="element-328" data-mp="11ne1wpxgu4qi">
                <dt className="type-small text-muted" data-id="element-329" data-mp="16ke5mu6ueveg">{stat.label}</dt>
                <dd className="mt-1 type-h5 text-ink" data-id="element-330" data-mp="1am94ls1awwp2">{stat.value}</dd>
              </div>)}
          </dl>
        </div>

        <div className="order-1 p-2 lg:order-2 lg:p-3" data-id="element-331" data-mp="38csivfyfzboj">
          <img src={destination.image} alt={`City view in ${destination.name}`} className="aspect-[4/3] h-full w-full rounded-[22px] object-cover md:rounded-[32px] lg:aspect-auto" data-id="element-332" data-mp="38edhphrshgs7" />
        </div>
      </div>
    </section>;
}